import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, AlertTitle } from '@mui/material';

/**
 * Contexto para gerenciar notificações/toasts na aplicação
 */
const NotificationContext = createContext(null);

/**
 * Provider de notificações
 */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  /**
   * Adiciona uma nova notificação
   * @param {Object} notification - Dados da notificação
   * @param {string} notification.message - Mensagem da notificação
   * @param {string} notification.severity - Tipo: 'success', 'error', 'warning', 'info'
   * @param {string} [notification.title] - Título opcional
   * @param {number} [notification.duration] - Duração em ms (padrão: 6000)
   */
  const showNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      message: notification.message || 'Notificação',
      severity: notification.severity || 'info',
      title: notification.title,
      duration: notification.duration || 6000,
    };

    setNotifications((prev) => [...prev, newNotification]);

    // Auto-remover após a duração especificada
    setTimeout(() => {
      removeNotification(id);
    }, newNotification.duration);
  }, []);

  /**
   * Remove uma notificação
   */
  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  /**
   * Helpers para tipos específicos de notificação
   */
  const showSuccess = useCallback((message, title) => {
    showNotification({ message, severity: 'success', title });
  }, [showNotification]);

  const showError = useCallback((message, title = 'Erro') => {
    showNotification({ message, severity: 'error', title, duration: 8000 });
  }, [showNotification]);

  const showWarning = useCallback((message, title) => {
    showNotification({ message, severity: 'warning', title });
  }, [showNotification]);

  const showInfo = useCallback((message, title) => {
    showNotification({ message, severity: 'info', title });
  }, [showNotification]);

  const value = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {/* Renderiza todas as notificações */}
      {notifications.map((notification) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={notification.duration}
          onClose={() => removeNotification(notification.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => removeNotification(notification.id)}
            severity={notification.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {notification.title && (
              <AlertTitle>{notification.title}</AlertTitle>
            )}
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};

/**
 * Hook para usar o contexto de notificações
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification deve ser usado dentro de NotificationProvider');
  }
  return context;
};

